<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-4">
            <h3 class="mb-0">Dati</h3>
            <div>
                <button
                    v-if="isEditing"
                    class="btn btn-secondary mr-3"
                    @click="cancel"
                    :disabled="isSaving"
                >
                    Annulla
                </button>
                <button
                    v-if="isEditing"
                    class="btn btn-primary"
                    @click="confirmSave"
                    :disabled="isSaving"
                >
                    <span v-if="isSaving" class="mr-2">
                        <div
                            class="spinner-border text-light"
                            style="height: 1rem; width: 1rem; border-width: 0.15em"
                            role="status"
                        >
                            <span class="sr-only">Loading...</span>
                        </div>
                    </span>
                    Salva
                </button>
                <button
                    v-if="!isEditing"
                    class="btn btn-outline-primary"
                    @click="edit"
                >
                    Modifica
                </button>
            </div>
        </div>
        <div class="row">
            <div class="col-md-8">
                <div class="card mb-4">
                    <div class="card-body">
                        <!-- created_at -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Data creazione</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{ formatDate(property.created_at) }}
                                </div>
                                <input
                                    v-else
                                    type="date"
                                    class="form-control"
                                    :value="
                                        formatDateForInput(property.created_at)
                                    "
                                    disabled
                                />
                            </div>
                        </div>

                        <!-- deal status -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Stato Deal</div>
                            <div class="col-9">
                                <div
                                    v-if="!isEditing"
                                    class="badge-status"
                                    :class="[
                                        findOptionKey(
                                            deal_status_options,
                                            property.deal_status,
                                            'badge'
                                        )
                                    ]"
                                >
                                    {{
                                        findOptionLabel(
                                            deal_status_options,
                                            property.deal_status
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.deal_status"
                                >
                                    <option
                                        v-for="o in deal_status_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- status -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Stato Lead</div>
                            <div class="col-9">
                                <div
                                    v-if="!isEditing"
                                    class="badge-status"
                                    :class="[
                                        findOptionKey(
                                            status_options,
                                            property.status,
                                            'badge'
                                        )
                                    ]"
                                >
                                    {{
                                        findOptionLabel(
                                            status_options,
                                            property.status
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.status"
                                >
                                    <option
                                        v-for="o in status_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- code -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Codice</div>
                            <div class="col-9">
                                <div v-if="!isEditing">{{ property.code }}</div>
                                <input
                                    v-else
                                    type="text"
                                    class="form-control"
                                    v-model="property.code"
                                    :disabled="!$page.props.can.admin"
                                />
                            </div>
                        </div>

                        <!-- municipality & address -->
                        <div v-if="!isEditing">
                            <div class="row align-items-center mb-3">
                                <div class="col-3">Città</div>
                                <div class="col-9">
                                    {{ property.municipality }}
                                </div>
                            </div>
                            <div class="row align-items-center mb-3">
                                <div class="col-3">Indirizzo</div>
                                <div class="col-9">{{ fullAddress }}</div>
                            </div>
                        </div>
                        <div v-else>
                            <div class="row align-items-center mb-3">
                                <div class="col-3">Indirizzo e Città</div>
                                <div class="col-9">
                                    <vue-google-autocomplete
                                        class="form-control"
                                        @place_changed="getAddressData"
                                        :placeholder="
                                            `${fullAddress}, ${property.municipality}`
                                        "
                                    ></vue-google-autocomplete>
                                </div>
                            </div>
                        </div>

                        <!-- source -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Fonte</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{
                                        findOptionLabel(
                                            source_options,
                                            property.source
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.source"
                                >
                                    <option
                                        v-for="o in source_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- partner -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Partner</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{ agencyNameById(property.agency_id) }}
                                </div>
                                <select v-else class="form-control" v-model="property.agency_id">
                                            <option v-for="a in agencies" :key="a.id" :value="a.id">
                                                {{ getPartnerName(a) }}
                                            </option>
                                    </select>
                            </div>
                        </div>

                        <!-- role -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Ruolo</div>
                            <div class="col-9">
                                <div v-if="!isEditing">{{ property.role }}</div>
                                <input
                                    v-else
                                    type="text"
                                    class="form-control"
                                    v-model="property.role"
                                />
                            </div>
                        </div>

                        <!-- inserted_by -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Committente</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{
                                        findOptionLabel(
                                            inserted_by_options,
                                            property.inserted_by
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.inserted_by"
                                >
                                    <option
                                        v-for="o in inserted_by_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- type -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Tipologia</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{
                                        findOptionLabel(
                                            type_options,
                                            property.type
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.type"
                                >
                                    <option
                                        v-for="o in type_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- conditions -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Stato di fatto</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{
                                        findOptionLabel(
                                            conditions_options,
                                            property.conditions
                                        )
                                    }}
                                </div>
                                <select
                                    v-else
                                    class="form-control"
                                    v-model="property.conditions"
                                >
                                    <option
                                        v-for="o in conditions_options"
                                        :key="o.value"
                                        :value="o.value"
                                        >{{ o.label }}</option
                                    >
                                </select>
                            </div>
                        </div>

                        <!-- assignee_id -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Assegnato a</div>
                            <div class="col-9">
                                <div v-if="!isEditing">{{ assigneeName }}</div>
                                <div v-show="isEditing">
                                    <select
                                        id="assignee_id"
                                        class="js-select2-custom custom-select"
                                        size="1"
                                        style="opacity: 0"
                                        :value="property.assignee_id"
                                    >
                                        <option :value="null" selected
                                            >Nessuno</option
                                        >
                                        <option
                                            v-for="assignable in assignables"
                                            :key="assignable.id"
                                            :value="assignable.id"
                                            >{{ assignable.name }}</option
                                        >
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- notes -->
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Note</div>
                            <div class="col-9">
                                <div v-if="!isEditing">
                                    {{ property.notes }}
                                </div>
                                <textarea
                                    v-else
                                    rows="4"
                                    class="form-control"
                                    v-model="property.notes"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md-4">
                <div class="card mb-4">
                    <div class="card-body">
                        <h3 class="mb-4">Dati Utente</h3>
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Nome</div>
                            <div class="col-9">
                                <div>
                                    {{ property.user_first_name }} {{ property.user_last_name }}
                                </div>
                            </div>
                        </div>
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Email</div>
                            <div class="col-9">
                                <div>
                                    {{ property.user_email }}
                                </div>
                            </div>
                        </div>
                        <div class="row align-items-center mb-3">
                            <div class="col-3">Telefono</div>
                            <div class="col-9">
                                <div>
                                    {{ property.user_phone }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import * as VueGoogleMaps from "vue2-google-maps";
import {
    findOptionLabel,
    findOptionKey,
    inserted_by_options,
    type_options,
    conditions_options,
    status_options,
    deal_status_options,
    source_options
} from "@/helpers/select-options";

export default {
    components: {
        "vue-google-autocomplete": VueGoogleMaps.Autocomplete
    },

    data() {
        return {
            isSaving: false,
            isEditing: false,
            property: null,
            report: null,
            propertyBk: null,
            assignables: null,
            agencies: [],

            inserted_by_options,
            type_options,
            conditions_options,
            status_options,
            source_options,
            deal_status_options
        };
    },

    beforeMount() {
        this.property = this.$page.props.property;
        this.report = this.$page.props.report;
        this.assignables = this.$page.props.assignables;
        this.agencies = this.$page.props.agencies;
    },

    mounted() {
        this.initSelect2();
    },

    computed: {
        fullAddress() {
            return `${this.property.address}, ${this.property.house_number}`;
        },
        assigneeName() {
            const assignee = this.assignables.find(
                a => a.id == this.property.assignee_id
            );
            if (assignee) return assignee.name;
            return "";
        }
    },

    methods: {
        findOptionLabel,
        findOptionKey,

        getPartnerName(agency) {
            if (agency.agency_name) return agency.agency_name;
            if (agency.company_name) return agency.company_name;
            return agency.name;
        },

        agencyNameById(id) {
            const a = this.agencies.find(ag => ag.id == id);
            if (!a) return "";
            if (a.agency_name) return a.agency_name;
            if (a.company_name) return a.company_name;
            return a.name;
        },

        initSelect2() {
            const self = this;
            const select2 = $(`#assignee_id`);
            $.HSCore.components.HSSelect2.init(select2);
            select2.on("select2:select", function(e) {
                var data = e.params.data;
                self.property.assignee_id =
                    data.id == "" ? null : parseInt(data.id);
            });
        },

        formatDate(date) {
            return DateTime.fromISO(date).toLocaleString(DateTime.DATE_FULL);
        },
        formatDateForInput(date) {
            return DateTime.fromISO(date).toISODate();
        },

        getAddressData(payload) {
            const city = this.getAddressComponent(payload, "locality");
            const address = this.getAddressComponent(payload, "route");
            const number = this.getAddressComponent(payload, "street_number");
            const postal_code = this.getAddressComponent(
                payload,
                "postal_code"
            );
            const province = this.getAddressComponent(
                payload,
                "administrative_area_level_2"
            );
            const region = this.getAddressComponent(
                payload,
                "administrative_area_level_1"
            );
            const country = this.getAddressComponent(payload, "country");
            this.property.municipality = city.short_name;
            this.property.address = address.short_name;
            this.property.house_number = number.short_name;
            this.property.postal_code = postal_code.short_name;
            this.property.province = province.short_name;
            this.property.region = region.short_name;
            this.property.country = country.short_name;
        },
        getAddressComponent(payload, type) {
            let { address_components } = payload;
            return address_components.filter(i => i.types.includes(type))[0];
        },

        edit() {
            this.propertyBk = _.cloneDeep(this.property);
            this.isEditing = true;
            $('[data-toggle="tooltip"]').tooltip();
        },
        confirmSave() {
            this.$confirm({
                message: `Salvare i dati?`,
                button: {
                    no: "No",
                    yes: "Si"
                },
                callback: confirm => {
                    if (confirm) {
                        this.save();
                    }
                }
            });
        },
        async save() {
            this.isSaving = true;

            try {
                const response = await this.$inertia.put(
                    `/admin/properties/${this.property.id}/data-section`,
                    this.property
                );
            } catch (error) {
                console.log(error);
            }

            this.isSaving = false;
            this.isEditing = false;
        },
        cancel() {
            this.property = _.cloneDeep(this.propertyBk);
            this.isEditing = false;
        }
    }
};
</script>

<style></style>
